import { useMemo } from 'react';
import { useDisplayTimeZone } from '@/stores/useTimezoneStore';
import {
  formatCalendarDate,
  formatDate,
  formatDateTime,
  formatRelative,
  formatTime,
  type DateInput,
} from '@/lib/datetime';

/**
 * Date/time formatters bound to the user's chosen display timezone.
 *
 * Use this everywhere a UTC timestamp from the API is shown to the user,
 * instead of raw `new Date(x).toLocaleString()` (which is locked to the
 * device zone and can't honor a chosen zone). The server stays UTC; this
 * is the single client-side place timezone is applied.
 *
 *   const { formatDateTime } = useDateFormat();
 *   <span>{formatDateTime(row.createdAt)}</span>
 */
export function useDateFormat() {
  const timeZone = useDisplayTimeZone();
  return useMemo(
    () => ({
      timeZone,
      formatDate: (v: DateInput, opts?: Intl.DateTimeFormatOptions) =>
        formatDate(v, timeZone, opts),
      // Calendar date (DOB, due dates) — timezone-less, identical for all viewers.
      formatCalendarDate: (v: DateInput, opts?: Intl.DateTimeFormatOptions) =>
        formatCalendarDate(v, opts),
      formatTime: (v: DateInput, opts?: Intl.DateTimeFormatOptions) =>
        formatTime(v, timeZone, opts),
      formatDateTime: (v: DateInput, opts?: Intl.DateTimeFormatOptions) =>
        formatDateTime(v, timeZone, opts),
      formatRelative: (v: DateInput) => formatRelative(v, timeZone),
    }),
    [timeZone],
  );
}
